"use client";

import type { CmsSidebarSectionFragment } from "@workspace/casino_core/queries/CmsSidebarQuery";
import CmsPhrase from "@workspace/cms-phrases/src/phrase/CmsPhrase";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from "@workspace/frontend-ui";
import { type FC, useState } from "react";
import DynamicHugeIcon from "../../../../utils/DynamicHugeIcon";
import { SIDEBAR_TOOLTIP_CLASS } from "../sidebarTooltip";
import SidebarCollapsibleGroupItem from "./SidebarCollapsibleGroupItem";
import SidebarCollapsibleTooltip from "./SidebarCollapsibleTooltip";
import SidebarIcon from "./SidebarIcon";

type Item = NonNullable<CmsSidebarSectionFragment["items"]>[number];

type Props = {
  section: CmsSidebarSectionFragment;
  items: Item[];
  brandSlug: string;
  isLoggedIn: boolean;
  dynamicSubitemsMap?: Record<string, React.ReactNode>;
  enabledFeatureFlags?: string[];
};

const SidebarCollapsibleGroup: FC<Props> = ({
  section,
  items,
  brandSlug,
  isLoggedIn,
  dynamicSubitemsMap,
  enabledFeatureFlags,
}) => {
  const { isMobile } = useSidebar();
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null);

  return (
    <Collapsible
      asChild
      defaultOpen={section.expanded}
      className="group/collapsible"
    >
      <SidebarMenuItem
        className="bg-secondary rounded-xl"
        data-cms-section={section.slug}
      >
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={{
              children: (
                <SidebarCollapsibleTooltip
                  section={section}
                  items={items}
                  brandSlug={brandSlug}
                />
              ),
              className: cn(SIDEBAR_TOOLTIP_CLASS, "p-1.5 min-w-58 rounded-xl"),
            }}
            size="md"
            className="text-md data-active:text-sidebar-primary hover:text-sidebar-primary group-data-[state=closed]/collapsible:bg-transparent rounded-xl"
          >
            <SidebarIcon icon={section.icon} hugeIcon={section.hugeIcon} />
            <CmsPhrase phrase={section.title} brandSlug={brandSlug} />
            <div className="ml-auto group-data-[state=open]/collapsible:bg-white/6 size-6 rounded-md flex items-center justify-center">
              <DynamicHugeIcon
                iconName="ArrowDown01Icon"
                size={20}
                className="transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
              />
            </div>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="gap-1 border-0 m-0 p-0">
            {items.map((item, index) => {
              const itemKey = item.id ?? item.slug ?? item.href ?? `${index}`;

              return (
                <SidebarCollapsibleGroupItem
                  key={itemKey}
                  item={item}
                  itemKey={itemKey}
                  brandSlug={brandSlug}
                  isLoggedIn={isLoggedIn}
                  isExpanded={expandedItemKey === itemKey}
                  isMobile={isMobile}
                  dynamicSubitems={
                    item.slug ? dynamicSubitemsMap?.[item.slug] : null
                  }
                  setExpandedItemKey={setExpandedItemKey}
                  enabledFeatureFlags={enabledFeatureFlags}
                />
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

export default SidebarCollapsibleGroup;
